// src/pages/ApplicationChampionshipSeason/shared/steps/Step1Info/Step1SeasonInfo.tsx
import { FunctionComponent } from 'react';
import { Box, Flex, Text, useToast } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SeasonType } from '@/model';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { usePostSeasonMutation, usePutSeasonMutation } from '@/services/queries/season';

import { SeasonFormData, SeasonWizardMode } from '../../schemas';
import { STANDINGS_COLUMN_KEYS } from '../../constants';
import { WizardStepFooter } from '../../components/WizardStepFooter';
import { ChampionshipSelectField } from './ChampionshipSelectField';
import { SeasonYearField } from './SeasonYearField';
import { SeasonFormatField } from './SeasonFormatField';
import { GroupsCountField } from './GroupsCountField';
import { StandingsColumnsField } from './StandingsColumnsField';

type Step1SeasonInfoProps = {
  mode: SeasonWizardMode;
  onAdvance: () => void;
  onCancel: () => void;
  /**
   * Quando a temporada já tem stages criadas, mudar o `type` (e o nº de grupos
   * no caso HYBRID) é destrutivo — desabilitamos esses campos e exibimos um
   * callout explicativo no Step 1.
   */
  isTypeLocked?: boolean;
};

const PREFIX = 'pages.application_season.components.season_step1';

export const Step1SeasonInfo: FunctionComponent<Step1SeasonInfoProps> = ({ mode, onAdvance, onCancel, isTypeLocked = false }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const form = useFormContext<SeasonFormData>();
  const seasonType = form.watch('season_type');
  const standingsColumns = form.watch('standings_columns');

  // Bloqueia avançar com a tabela de classificação 100% invisível.
  const hasVisibleColumn = STANDINGS_COLUMN_KEYS.some(
    key => standingsColumns?.[key] !== false,
  );

  const postSeason = usePostSeasonMutation();
  const putSeason = usePutSeasonMutation();

  const handleNext = async () => {
    form.setValue('step', 1);

    const isValid = await form.trigger();
    if (!isValid) return;

    const values = form.getValues();

    if (mode === 'edit') {
      if (!values.season_uuid) {
        toast({ status: 'error', title: t(`${PREFIX}.toasts.missing_season_id`) });
        return;
      }

      // Pula o PUT quando nenhum campo da Season mudou — em Edit, na prática
      // só `season_year` (e raramente `season_type`/`groups_count` quando ainda
      // não há stages) costuma ser editado. `championship_uuid` é read-only.
      const dirty = form.formState.dirtyFields;
      const hasSeasonChanges =
        !!dirty.season_year ||
        !!dirty.season_type ||
        !!dirty.championship_uuid ||
        !!dirty.is_filtered ||
        !!dirty.is_active ||
        !!dirty.standings_columns;

      if (!hasSeasonChanges) {
        onAdvance();
        return;
      }

      putSeason.mutate(
        {
          id: values.season_uuid,
          payload: {
            championship_uuid: values.championship_uuid as string,
            year: Number(values.season_year),
            type: values.season_type as SeasonType,
            is_filtered: values.is_filtered,
            is_active: values.is_active,
            standings_columns: values.standings_columns,
          },
        },
        {
          onSuccess: updated => {
            form.setValue('season_uuid', updated.uuid);
            onAdvance();
          },
          onError: (error: unknown) => {
            toast({
              status: 'error',
              title: (error as Error)?.message || t(`${PREFIX}.toasts.update_error`),
            });
          },
        },
      );
      return;
    }

    postSeason.mutate(
      {
        championship_uuid: values.championship_uuid as string,
        year: Number(values.season_year),
        type: values.season_type as SeasonType,
        is_filtered: values.is_filtered,
        is_active: values.is_active,
        standings_columns: values.standings_columns,
      },
      {
        onSuccess: created => {
          form.setValue('season_uuid', created.uuid);
          onAdvance();
        },
        onError: (error: unknown) => {
          toast({
            status: 'error',
            title: (error as Error)?.message || t(`${PREFIX}.toasts.create_error`),
          });
        },
      },
    );
  };

  return (
    <Flex direction='column' gap={6}>
      <Text fontSize='18px' fontWeight='semibold'>
        {t(`${PREFIX}.title`)}
      </Text>

      <Flex direction='column' gap={6} maxW={{ base: '100%', lg: '680px' }}>
        <Flex direction={{ base: 'column', md: 'row' }} gap={5} align='flex-start'>
          <Box flex='1' w='100%'>
            <ChampionshipSelectField readOnly={mode === 'edit'} />
          </Box>
          <Box w={{ base: '100%', md: '160px' }}>
            <SeasonYearField />
          </Box>
        </Flex>

        <SeasonFormatField isLocked={isTypeLocked} />

        {seasonType === SeasonType.HYBRID ? <GroupsCountField isLocked={isTypeLocked} /> : null}

        <StandingsColumnsField />
      </Flex>

      <WizardStepFooter
        onCancel={onCancel}
        primaryLabel={t('pages.application_season.components.season_wizard.footer.next')}
        onPrimary={handleNext}
        primaryLoading={postSeason.isPending || putSeason.isPending}
        primaryDisabled={!hasVisibleColumn}
        primaryRightIcon={<ChakraFeatherIcon name='arrow-right' boxSize='16px' />}
      />
    </Flex>
  );
};
